import { cn } from '@repo/lib/cn';
import { endOfDay, format, startOfDay, subMonths, subYears } from 'date-fns';
import { ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button, ButtonGroup } from '@/components/ui/button';
import { DatePickerCalendar } from '@/components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle';

export interface DateRangeFilterValue {
  start?: Date | string;
  end?: Date | string;
}

const PRESETS = [
  {
    value: '1m',
    label: '1m',
    getStart: () => startOfDay(subMonths(new Date(), 1)),
    getEnd: () => endOfDay(new Date()),
  },
  {
    value: '4m',
    label: '4m',
    getStart: () => startOfDay(subMonths(new Date(), 4)),
    getEnd: () => endOfDay(new Date()),
  },
  {
    value: '6m',
    label: '6m',
    getStart: () => startOfDay(subMonths(new Date(), 6)),
    getEnd: () => endOfDay(new Date()),
  },
  {
    value: '1y',
    label: '1r',
    getStart: () => startOfDay(subYears(new Date(), 1)),
    getEnd: () => endOfDay(new Date()),
  },
] as const;

const toDate = (value: Date | string | undefined): Date | undefined => {
  if (!value) return undefined;
  return typeof value === 'string' ? new Date(value) : value;
};

const isSameDay = (a: Date | undefined, b: Date | undefined): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
};

interface DateRangePickerProps {
  value: DateRangeFilterValue | undefined;
  onChange: (value: DateRangeFilterValue | undefined) => void;
}

export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [activeTarget, setActiveTarget] = useState<'start' | 'end'>('start');

  const start = toDate(value?.start);
  const end = toDate(value?.end);
  const hasFilter = !!(start || end);

  const activePresetValue = useMemo(() => {
    if (!start && !end) return 'all';
    return PRESETS.find((p) => isSameDay(start, p.getStart()) && isSameDay(end, p.getEnd()))?.value;
  }, [start, end]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <ButtonGroup>
        <PopoverTrigger asChild>
          <Button variant="secondary" size="sm">
            <CalendarIcon />
            {hasFilter && (
              <>
                {start ? format(start, 'dd.MM.yyyy') : '-'}
                <ArrowRightIcon className="size-3! shrink-0" />
                {end ? format(end, 'dd.MM.yyyy') : '-'}
              </>
            )}
          </Button>
        </PopoverTrigger>
      </ButtonGroup>

      <PopoverContent className="w-80 p-0">
        <div>
          <div className="p-2">
            <ToggleGroup
              type="single"
              value={activePresetValue}
              variant="outline"
              groupVariant="separate"
              size="sm"
              className="w-full flex-wrap"
              onValueChange={(next) => {
                if (!next) return;
                if (next === 'all') {
                  onChange(undefined);
                  return;
                }
                const preset = PRESETS.find((p) => p.value === next);
                if (preset) onChange({ start: preset.getStart(), end: preset.getEnd() });
              }}
            >
              {PRESETS.map((p) => (
                <ToggleGroupItem key={p.value} value={p.value}>
                  {p.label}
                </ToggleGroupItem>
              ))}
              <ToggleGroupItem value="all">Wszystkie</ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="group relative flex h-24 min-h-24 w-full items-stretch p-2">
            <div className="flex h-full w-full items-stretch gap-3">
              <DateRangeButton label="Od" date={start} active={activeTarget === 'start'} onClick={() => setActiveTarget('start')} />

              <DateRangeButton label="Do" date={end} active={activeTarget === 'end'} onClick={() => setActiveTarget('end')} />
            </div>
          </div>

          <div className="border-t p-2">
            <DatePickerCalendar
              selected={activeTarget === 'start' ? start : end}
              onSelect={(date) => {
                if (activeTarget === 'start') {
                  onChange({
                    start: date ? startOfDay(date) : undefined,
                    end: end ? endOfDay(end) : undefined,
                  });
                  return;
                }

                onChange({
                  start: start ? startOfDay(start) : undefined,
                  end: date ? endOfDay(date) : undefined,
                });
              }}
              matcher={activeTarget === 'start' ? (end ? { after: endOfDay(end) } : undefined) : start ? { before: startOfDay(start) } : undefined}
              className="w-full border-none p-0"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface DateRangeButtonProps {
  label: string;
  date: Date | undefined;
  active: boolean;
  onClick: () => void;
  className?: string;
}

function DateRangeButton({ label, date, active, onClick, className }: DateRangeButtonProps) {
  return (
    <button
      type="button"
      data-state={date ? 'filled' : 'empty'}
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      className={cn(
        'peer flex flex-1 cursor-pointer flex-col items-start justify-center rounded-md border px-4 transition-all duration-150 select-none',
        'hover:bg-muted/50 border-transparent',
        'data-[active=true]:bg-background data-[active=true]:hover:bg-muted/25 data-[active=true]:border-border-primary data-[active=true]:shadow-sm',
        'transition-outline outline-focus-ring focus-visible:z-10 focus-visible:outline-3',
        className
      )}
    >
      <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">{label}</p>
      {date ? <p className="text-foreground text-sm leading-tight font-semibold">{format(date, 'LLL dd, y')}</p> : <p className="text-muted-foreground text-sm font-medium">Nie wybrano</p>}
    </button>
  );
}
